/**
 * Link processing utilities for article content
 * Handles external link attributes (nofollow, sponsored, target)
 * Includes XSS sanitization for security
 */

import { sanitizeHtml } from './sanitize';

/**
 * Check if a URL is internal (same domain or relative)
 */
export function isInternalLink(href: string): boolean {
  if (!href) return true;

  // Relative URLs are internal
  if (
    href.startsWith('/') ||
    href.startsWith('#') ||
    href.startsWith('./') ||
    href.startsWith('../')
  ) {
    return true;
  }

  // Check for dinamani.com domains
  const internalDomains = [
    'dinamani.com',
    'www.dinamani.com',
    'dinamani.mo.vc',
    'api.dinamani.mo.vc',
    'builderapi.dinamani.com',
  ];

  try {
    const url = new URL(href);
    return internalDomains.some(
      (domain) => url.hostname === domain || url.hostname.endsWith('.' + domain)
    );
  } catch {
    // If URL parsing fails, treat as internal (likely a relative URL)
    return true;
  }
}

/**
 * Process HTML content to add appropriate rel and target attributes to links
 *
 * Internal links: target="_self" (opens in same tab)
 * External links: target="_blank" and rel only when CMS/API provides it
 *
 * Links can have data-rel attribute to specify custom rel value:
 * - data-rel="sponsored" -> rel="sponsored"
 * - data-rel="sponsored nofollow" -> rel="sponsored nofollow"
 * - data-rel="nofollow" -> rel="nofollow"
 *
 * @param html - The HTML content to process
 * @returns Processed HTML with proper link attributes
 */
export function processArticleLinks(html: string): string {
  if (!html) return '';

  // SECURITY: Sanitize HTML to prevent XSS before processing links
  const sanitizedHtml = sanitizeHtml(html);

  // Use regex to find and process anchor tags
  // This handles the HTML string manipulation without a DOM parser (works on server-side)
  return (
    sanitizedHtml
      .replace(/<a\s+([^>]*)>/gi, (match, attributes) => {
        // Extract href
        const hrefMatch = attributes.match(/href\s*=\s*["']([^"']*)["']/i);
        const href = hrefMatch ? hrefMatch[1] : '';

        // Extract existing data-rel if present (for CMS-specified rel values)
        const dataRelMatch = attributes.match(
          /data-rel\s*=\s*["']([^"']*)["']/i
        );
        const dataRel = dataRelMatch ? dataRelMatch[1] : null;

        // Check if already has rel attribute
        const existingRelMatch = attributes.match(
          /rel\s*=\s*["']([^"']*)["']/i
        );
        const existingRel = existingRelMatch ? existingRelMatch[1] : '';

        // Check if already has target attribute
        const existingTargetMatch = attributes.match(
          /target\s*=\s*["']([^"']*)["']/i
        );

        const isInternal = isInternalLink(href);

        let newAttributes = attributes;

        if (isInternal) {
          // Internal link: ensure target="_self" (or no target)
          if (existingTargetMatch) {
            newAttributes = newAttributes.replace(
              /target\s*=\s*["'][^"']*["']/i,
              'target="_self"'
            );
          } else {
            newAttributes = newAttributes + ' target="_self"';
          }
        } else {
          // External link: set target="_blank" and preserve CMS/API rel when present

          // Set target="_blank"
          if (existingTargetMatch) {
            newAttributes = newAttributes.replace(
              /target\s*=\s*["'][^"']*["']/i,
              'target="_blank"'
            );
          } else {
            newAttributes = newAttributes + ' target="_blank"';
          }

          // Determine rel value from the CMS/API only.
          let relValue = '';

          if (dataRel) {
            // Use CMS-specified rel value
            relValue = dataRel;
          } else if (existingRel) {
            // Keep existing rel exactly as the API provided it
            relValue = existingRel;
          }

          if (relValue) {
            // Update or add rel attribute exactly as the CMS/API provided it.
            if (existingRelMatch) {
              newAttributes = newAttributes.replace(
                /rel\s*=\s*["'][^"']*["']/i,
                `rel="${relValue.trim()}"`
              );
            } else {
              newAttributes = newAttributes + ` rel="${relValue.trim()}"`;
            }
          }
        }

        return `<a ${newAttributes.trim()}>`;
      })
      // Add inline styles for list elements (overrides Tailwind Preflight reset)
      .replace(
        /<ul>/gi,
        '<ul style="list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem;">'
      )
      .replace(
        /<ol>/gi,
        '<ol style="list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem;">'
      )
      .replace(
        /<li>/gi,
        '<li style="margin-bottom: 0.5rem; display: list-item;">'
      )
  );
}

/**
 * Hook wrapper for use in components that need link processing
 * Can be used with useMemo for performance
 */
export function useProcessedHtml(html: string): string {
  return processArticleLinks(html);
}
